'use client';

import { Avatar, Box, Paper, Typography, useTheme } from '@mui/material';

import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { Star } from '@/components/Reviews/ReviewComponent';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import { hasRichTextContent } from '@/lib/helpers/contentHelpers';
import { getImageUrl } from '@/lib/helpers/getImageUrl';
import {
  ComponentBlocksAuthorDetails,
  Enum_Componentshareddisplayoptions_Contentswidth,
  TeamMember,
} from '@/types';

export default function AuthorCard({
  WrittenBy,
  ReviewedBy,
  WrittenByOverride,
  ReviewedByOverride,
  AuthorSectionDetails,
  AuthorSectionStat,
  AuthorDetailsDisplayOptions,
  AuthorDetailsBackground,
  ShowWrittenByLabel,
  ShowReviewedByLabel,
}: ComponentBlocksAuthorDetails) {
  const theme = useTheme();

  const authors = [
    {
      label: 'Written By',
      person: WrittenBy || null,
      overrideBio: WrittenByOverride,
      showLabel: ShowWrittenByLabel !== false,
    },
    {
      label: 'Reviewed By',
      person: ReviewedBy || null,
      overrideBio: ReviewedByOverride,
      showLabel: ShowReviewedByLabel !== false,
    },
  ].filter((a) => a.person);

  const hasDetails = hasRichTextContent(AuthorSectionDetails);

  const hasStat = typeof AuthorSectionStat === 'string' && AuthorSectionStat.trim().length > 0;

  if (authors.length === 0 && !hasDetails && !hasStat) return null;

  return (
    <CustomBackground
      backgroundOptions={(AuthorDetailsBackground ?? undefined) as StrapiBackgroundOptions}
      displayOptions={
        {
          ...AuthorDetailsDisplayOptions,
          ContentsWidth:
            AuthorDetailsDisplayOptions?.ContentsWidth ??
            Enum_Componentshareddisplayoptions_Contentswidth.Wide,
          PaddingTop: AuthorDetailsDisplayOptions?.PaddingTop ?? 'md',
          PaddingBottom: AuthorDetailsDisplayOptions?.PaddingBottom ?? 'md',
          PaddingInline: AuthorDetailsDisplayOptions?.PaddingInline ?? 'sm',
        } as StrapiDisplayOptions
      }
      boxProps={{
        // @ts-expect-error - this does work, but types are not correct
        'data-component': 'AuthorCard',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 1.5, sm: 2 },
        }}
      >
        {/* Optional top blurb / stat */}
        {(hasDetails || hasStat) && (
          <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3 } }}>
            {hasDetails && (
              <Box
                sx={{
                  maxWidth: '750px',
                  mx: 'auto',
                  mb: theme.spacing(3),
                  textAlign: 'center',
                }}
              >
                <GlobalBlockRenderer content={AuthorSectionDetails || []} />
              </Box>
            )}

            {hasStat &&
              (() => {
                const statMatch = AuthorSectionStat.match(/^(\d(?:\.\d+)?)\s*\/\s*5$/);
                const rating = statMatch ? parseFloat(statMatch[1]) : null;

                return (
                  <>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        color: theme.palette.text.primary,
                        fontSize: '1.15rem',
                        lineHeight: 1.4,
                      }}
                    >
                      {AuthorSectionStat}
                    </Typography>

                    {rating !== null && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: 0.5,
                          mb: 2,
                        }}
                      >
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Box
                            key={i}
                            sx={{
                              opacity: i < Math.round(rating) ? 1 : 0.25,
                              transition: 'opacity 0.2s',
                            }}
                          >
                            <Star id={`rating-star-${i}`} />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </>
                );
              })()}
          </Box>
        )}

        {/* Author and Reviewer cards */}
        {authors.map(({ label, person, overrideBio, showLabel }, index) => (
          <AuthorCardItem
            key={index}
            label={label}
            person={person!}
            overrideBio={overrideBio}
            showLabel={showLabel}
          />
        ))}
      </Box>
    </CustomBackground>
  );
}

function AuthorCardItem({
  label,
  person,
  overrideBio,
  showLabel,
}: {
  label: string;
  person: TeamMember;
  overrideBio?: any;
  showLabel: boolean;
}) {
  const theme = useTheme();
  const { Name, Role, Avatar: Photo } = person || {};
  const photoUrl = getImageUrl({ image: Photo, size: 'small' });
  const hasPhoto = Boolean(photoUrl);

  const hasOverrideBio = hasRichTextContent(overrideBio);

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'center', sm: 'flex-start' },
        textAlign: { xs: 'center', sm: 'left' },
        gap: { xs: 1.5, sm: 2 },
        px: { xs: 2.5, sm: 4 },
        borderRadius: 4,
        border: `0.15rem solid ${theme.palette.purple[500]}`,
        boxShadow: '0 3px 10px rgba(146, 137, 216, 0.12), 0 1px 2px rgba(0,0,0,0.04)',
        transition: 'transform 0.25s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: { xs: 'none', sm: 'translateY(-3px)' },
          boxShadow: {
            xs: '0 3px 10px rgba(146, 137, 216, 0.12)',
            sm: '0 6px 20px rgba(146, 137, 216, 0.25)',
          },
        },
      }}
    >
      {/* === Avatar Section === */}
      <Avatar
        src={photoUrl ?? undefined}
        alt={Name || 'Author Avatar'}
        sx={{
          width: { xs: 80, sm: 90 },
          height: { xs: 80, sm: 90 },
          fontSize: 28,
          fontWeight: 700,
          bgcolor: hasPhoto ? theme.palette.purple[600] : theme.palette.purple[200],
          color: hasPhoto ? '#fff' : theme.palette.purple[900],
          flexShrink: 0,
          border: `3px solid ${theme.palette.common.white}`,
          boxShadow: '0 0 0 3px rgba(146, 137, 216, 0.15)',
          mb: { xs: 1, sm: 0 },
        }}
      >
        {Name?.charAt(0)}
      </Avatar>

      {/* === Text Content === */}
      <Box
        sx={{
          flex: 1,
          width: '100%',
        }}
      >
        {/* Label (Written By / Reviewed By) */}
        {showLabel && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 1,
              color: theme.palette.purple[700],
              mb: 0.75,
              fontSize: { xs: '0.75rem', sm: '0.8rem' },
              textAlign: 'left',
            }}
          >
            {label}
          </Typography>
        )}

        {hasOverrideBio ? (
          <Box sx={{ mt: 1, width: '100%' }}>
            <GlobalBlockRenderer content={overrideBio || []} />
          </Box>
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: { xs: '0.95rem', sm: '1rem' },
              lineHeight: 1.7,
              width: '100%',
            }}
          >
            {person.Bio}
          </Typography>
        )}

        {/* Name + Role */}
        <Box
          sx={{
            textAlign: 'left',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1rem', sm: '1.1rem' },
              lineHeight: 1.4,
              color: theme.palette.text.primary,
              mb: 1,
            }}
          >
            {Name}
            {Role && (
              <Typography
                component="span"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  fontSize: 'inherit',
                }}
              >
                {`, ${Role}`}
              </Typography>
            )}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}
